import { useCallback, useEffect, useState } from "react";
import { Hand, Check, X, Undo2, Megaphone } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { fmtShift, fmtWhen } from "./shiftFormat";

// v2.8+E347 · dropped shifts anyone can claim (first claim holds it, a supervisor approves).
export const OpenShiftsBoard = ({ refreshKey, onChanged }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminApi.get("/admin/open-shifts");
      setData(d);
      setFailed(false);
    } catch (_e) { setFailed(true); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (id, action, msg) => {
    try {
      await adminApi.post(`/admin/open-shifts/${id}/${action}`, {});
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the shift");
      await load();
    }
  };

  if (failed && !data) {
    return (
      <Card className="p-4 text-sm text-rose-500" data-testid="open-shifts-error">
        Couldn't load open shifts right now — refresh to try again.
      </Card>
    );
  }
  if (!data) return null;
  const { shifts = [], is_manager } = data;

  return (
    <Card className="p-4" data-testid="open-shifts-board">
      <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
        <Megaphone className="h-4 w-4"/>Open shifts ({shifts.length})
      </div>
      {shifts.length === 0 ? (
        <div className="text-sm text-zinc-400 py-4 text-center" data-testid="open-shifts-empty">No open shifts right now.</div>
      ) : (
        <ul className="space-y-2" data-testid="open-shifts-list">
          {shifts.map((s) => (
            <li key={s.id} data-testid={`open-shift-${s.id}`} className="flex items-start justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{fmtShift(s)}</div>
                <div className="text-[11px] text-zinc-500">
                  {s.opened_from ? `Dropped by ${s.opened_from}` : "Open shift"}{s.note ? ` · ${s.note}` : ""}
                </div>
                {s.claim && (
                  <div className="text-[11px] text-amber-700 dark:text-amber-300 font-semibold mt-0.5" data-testid={`open-shift-claim-${s.id}`}>
                    {s.mine_claim ? "You claimed this" : `Claimed by ${s.claim.staff_name}`} · {fmtWhen(s.claim.at, { weekday: undefined })} · waiting for supervisor
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!s.claim && (
                  <Button size="sm" onClick={() => act(s.id, "claim", "Claimed — a supervisor will approve it")} data-testid={`open-shift-claim-btn-${s.id}`} className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                    <Hand className="h-3.5 w-3.5 mr-1"/>Claim
                  </Button>
                )}
                {s.mine_claim && (
                  <Button size="sm" variant="ghost" onClick={() => act(s.id, "unclaim", "Claim withdrawn")} data-testid={`open-shift-unclaim-${s.id}`} className="h-8 text-xs text-zinc-500">
                    <Undo2 className="h-3.5 w-3.5 mr-1"/>Withdraw
                  </Button>
                )}
                {is_manager && s.claim && !s.mine_claim && (
                  <>
                    <Button size="sm" onClick={() => act(s.id, "approve", `Shift given to ${s.claim.staff_name}`)} data-testid={`open-shift-approve-${s.id}`} className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white">
                      <Check className="h-3.5 w-3.5 mr-1"/>Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => act(s.id, "reject", "Claim rejected — shift is open again")} data-testid={`open-shift-reject-${s.id}`} className="h-8 text-xs">
                      <X className="h-3.5 w-3.5 mr-1"/>Reject
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default OpenShiftsBoard;
